import { ApiResponse } from "../schemas/apiResponseSchema";
import apiClient from "../clients/apiClient";

// ========================================
// Reference data types (from other tables)
// ========================================

export interface CustomerGroupResponse {
    id?: number | null;
    group_name?: string | null;
    group_description?: string | null;
    active?: boolean | null;
}

// ========================================
// pos_promodet - Offer Criteria Row
// ========================================

export interface PromoDetRequest {
    id?: number;
    line_seq: number;
    criteria_type: 'item_group' | 'item' | 'amount_range' | 'payment_mode' | 'location' | 'customer_group';
    criteria_value: string; // comma-separated codes/IDs
    amount_min?: number | null;
    amount_max?: number | null;
}

export interface PromoDetResponse {
    id?: number;
    promo_id?: number;
    line_seq: number;
    criteria_type: string;
    criteria_value?: string | null;
    amount_min?: number | null;
    amount_max?: number | null;
}

// ========================================
// pos_promohed - Promotion Header
// ========================================

export interface PromoHedRequest {
    promo_code: string;
    promo_name: string;
    promo_desc?: string;
    promo_type_id: number;
    promo_datefrom?: string | null;
    promo_dateto?: string | null;
    promo_timefrom?: string | null;
    promo_timeto?: string | null;
    promo_days?: number | null; // bitmask: bit0=Sun...bit6=Sat
    promo_active: boolean;
    promo_disper?: number | null;
    promo_disamt?: number | null;
    promo_free_item?: string | null;
    promo_free_voucher?: string | null;
    details: PromoDetRequest[]; // pos_promodet rows
}

export type PromoStatus = 'pending' | 'approved' | 'rejected';

export interface PromoHedResponse {
    id?: number;
    promo_code: string;
    promo_name: string;
    promo_type_code?: string | null;
    promo_desc?: string | null;
    promo_type_id?: number | null;
    promo_datefrom?: string | null;
    promo_dateto?: string | null;
    promo_timefrom?: string | null;
    promo_timeto?: string | null;
    promo_days?: number | null;
    promo_active: boolean;
    promo_status: PromoStatus;
    promo_approved_by?: string | null;
    promo_approved_at?: string | null;
    promo_reject_reason?: string | null;
    promo_disper?: number | null;
    promo_disamt?: number | null;
    promo_free_item?: string | null;
    promo_free_voucher?: string | null;
    details?: PromoDetResponse[];
    promo_crby?: string | null;
    promo_crat?: string | null;
    promo_mdby?: string | null;
    promo_mdat?: string | null;
}

// ========================================
// API Functions
// ========================================

export async function getAllPromotions(): Promise<PromoHedResponse[]> {
    try {
        const response: ApiResponse<PromoHedResponse[]> = (await apiClient.get('/promotions')).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Fetching promotions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPromotionById(id: number): Promise<PromoHedResponse> {
    try {
        const response: ApiResponse<PromoHedResponse> = (await apiClient.get(`/promotions/${id}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Fetching promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function createPromotion(data: PromoHedRequest): Promise<PromoHedResponse> {
    try {
        const response: ApiResponse<PromoHedResponse> = (await apiClient.post('/promotions', data)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Creating promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function updatePromotion(id: number, data: Partial<PromoHedRequest>): Promise<PromoHedResponse> {
    try {
        const response: ApiResponse<PromoHedResponse> = (await apiClient.put(`/promotions/${id}`, data)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Updating promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function deletePromotion(id: number): Promise<void> {
    try {
        await apiClient.delete(`/promotions/${id}`);
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Deleting promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}

// ========================================
// Promotion Approval Workflow
// ========================================

export async function getPendingPromotions(): Promise<PromoHedResponse[]> {
    try {
        const response: ApiResponse<PromoHedResponse[]> = (await apiClient.get('/promotions/pending')).data;
        return response.data || [];
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Fetching pending promotions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function approvePromotion(id: number): Promise<PromoHedResponse> {
    try {
        const response: ApiResponse<PromoHedResponse> = (await apiClient.put(`/promotions/${id}/approve`, {})).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || error.response.data.detail || 'Approving promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function rejectPromotion(id: number, reason?: string): Promise<PromoHedResponse> {
    try {
        const response: ApiResponse<PromoHedResponse> = (await apiClient.put(`/promotions/${id}/reject`, { reason })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || error.response.data.detail || 'Rejecting promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}

// ========================================
// Promotion Types
// ========================================

export interface PromotionTypeListItem {
    id: number;
    type_code: string;
    type_name: string;
    type_desc?: string | null;
    criteria_pattern?: string[] | null;
    type_priority: number;
}

export async function getPromotionTypes(): Promise<PromotionTypeListItem[]> {
    try {
        const response: ApiResponse<PromotionTypeListItem[]> = (await apiClient.get('/promotion-types')).data;
        return response.data || [];
    } catch {
        return [];
    }
}

// ========================================
// Reference Data Fetching
// ========================================

export async function getAllCustomerGroups(): Promise<CustomerGroupResponse[]> {
    try {
        const response: ApiResponse<CustomerGroupResponse[]> = (await apiClient.get('/pos_customer_group/all')).data;
        return response.data || [];
    } catch {
        return [];
    }
}

export async function searchItems(search: string): Promise<{ item_code: string; item_name: string }[]> {
    try {
        const response: ApiResponse<any[]> = (await apiClient.get('/pos_item/all', {
            params: { search, limit: 50 },
            headers: { 'x-skip-auth-redirect': 'true' },
        })).data;
        return (response.data || []).map((item: any) => ({
            item_code: item.item_code || '',
            item_name: item.item_name || '',
        }));
    } catch {
        return [];
    }
}

// ========================================
// Automatic Promotion Selection
// ========================================

export interface CartItemRequest {
    itemCode?: string | null;
    itemGroup?: string | null;
    qty?: number | null;
}

export interface PromotionSelectionRequest {
    trigger?: 'member_changed' | 'paymode_selected' | 'card_type_selected';
    activeTypeCodes: string[];
    paymode?: string | null;
    cardTypeCode?: string | null;
    paymodes: string[];
    cartItems: CartItemRequest[];
    subtotal: number;
    customerGroupId?: number | null;
    locationId?: string | null;
}

export interface PromotionSelectionResponse {
    promo_id?: number | null;
    promo_code?: string | null;
    promo_name?: string | null;
    promo_type_code?: string | null;
    promo_disper?: number | null;
    promo_disamt?: number | null;
    promo_free_item?: string | null;
    promo_free_voucher?: string | null;
    details?: PromoDetResponse[];
}

export interface PromotionSelectionListResponse {
    promotions: PromotionSelectionResponse[];
}

/**
 * Automatically select the best matching promotion based on cart context.
 */
export async function selectPromotion(request: PromotionSelectionRequest): Promise<PromotionSelectionResponse[]> {
    try {
        const response: ApiResponse<PromotionSelectionListResponse> = (
            await apiClient.post('/promotions/select', request)
        ).data;
        return response.data?.promotions || [];
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.message || 'Selecting promotion failed');
        }
        throw new Error('network error, please contact support team');
    }
}
